"use client";

import React, { useEffect, useState } from "react";
import { Calendar, User, Clock, ArrowRight, Share2, Tag, ChevronRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from "@/components/ui/card";



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="32763" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-80 w-full animate-pulse rounded-none bg-muted/50" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null; 
  }

  return (
    <div className="mt-16 border-t border-border pt-12">
      <h3 className="mb-8 text-2xl font-bold tracking-tighter text-foreground uppercase">More Building Insights</h3>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {posts.map((post, i) => (
          <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden border-border bg-card transition-all hover:border-foreground rounded-none">
            {post.featuredImage && (
              <div className="relative aspect-video w-full overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            )}
            <CardHeader className="p-6 pb-3">
              <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                {post.categories && post.categories.length > 0 && (
                  <span className="font-semibold text-foreground uppercase tracking-widest">
                    {post.categories[0].name}
                  </span>
                )}
                {post.date && (
                  <>
                    <span className="opacity-50">•</span>
                    <span className="uppercase tracking-wider">{new Date(post.date).toLocaleDateString()}</span>
                  </>
                )}
              </div>
              <CardTitle className="line-clamp-2 text-lg font-bold tracking-tight group-hover:text-primary transition-colors">
                <Link to={post.link || "#"}>{post.title}</Link>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-grow px-6 py-0">
              <div 
                className="line-clamp-3 text-sm text-muted-foreground leading-relaxed"
                dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
              />
            </CardContent>
            <CardFooter className="p-6 pt-4">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="outline" className="w-full justify-between rounded-none border-border group-hover:border-foreground group-hover:bg-foreground group-hover:text-background transition-all uppercase text-xs font-bold tracking-widest">
                  View Details <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}



function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="mb-8 h-12 w-3/4 animate-pulse rounded-none bg-muted/50" />
        <div className="mb-12 h-96 w-full animate-pulse rounded-none bg-muted/50" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded-none bg-muted/50" />
          <div className="h-4 w-full animate-pulse rounded-none bg-muted/50" />
          <div className="h-4 w-2/3 animate-pulse rounded-none bg-muted/50" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto flex min-h-[50vh] max-w-4xl flex-col items-center justify-center px-4 text-center">
        <h2 className="text-2xl font-bold text-destructive uppercase tracking-tighter">Project Load Error</h2>
        <p className="mt-2 text-muted-foreground">We encountered an issue retrieving our construction portfolio data.</p>
        <Link to="/NorthWestLondonExpertBuilderPage" className="mt-6">
          <Button className="rounded-none uppercase tracking-widest">Return Home</Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto flex min-h-[50vh] max-w-4xl flex-col items-center justify-center px-4 text-center">
        <h2 className="text-2xl font-bold uppercase tracking-tighter">Project Not Found</h2>
        <p className="mt-2 text-muted-foreground">The specific building project or article requested is unavailable.</p>
        <Link to="/NorthWestLondonExpertBuilderPage" className="mt-6">
          <Button className="rounded-none uppercase tracking-widest">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  
  const relatedQuery = new WP_Query({
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [post.id],
    orderby: "date",
    order: "DESC",
    
    category__in: post.categories?.map((c) => c.id) || [],
  });

  return (
    <article className="container mx-auto max-w-4xl px-4 py-12 md:py-20">
      {}
      <header className="mb-10 text-center md:mb-14">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-3 text-xs font-bold tracking-widest uppercase text-muted-foreground">
          {post.categories && post.categories.length > 0 && (
            <Link to={post.categories[0].link || "#"}>
              <span className="rounded-none border border-border px-3 py-1 text-foreground hover:bg-foreground hover:text-background transition-colors">
                {post.categories[0].name}
              </span>
            </Link>
          )}
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {post.date ? new Date(post.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : ""}
          </span>
          <span className="hidden md:inline opacity-30">•</span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            4 min read
          </span>
        </div>
        
        <h1 className="text-3xl font-extrabold leading-tight tracking-tighter text-foreground md:text-4xl lg:text-6xl uppercase">
          {post.title}
        </h1>

        {post.excerpt && (
          <div 
            className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed italic"
            dangerouslySetInnerHTML={{ __html: post.excerpt }}
          />
        )}

        {}
        {post.author && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <Avatar className="h-12 w-12 border border-border rounded-none">
              <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} className="rounded-none" />
              <AvatarFallback className="rounded-none bg-muted"><User className="h-5 w-5" /></AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-xs font-bold uppercase tracking-widest text-foreground">{post.author.name}</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Project Lead</p>
            </div>
          </div>
        )}
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-none border border-border md:mb-16">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover grayscale hover:grayscale-0 transition-all duration-1000"
          />
        </div>
      )}

      {}
      <div className="prose prose-lg prose-stone dark:prose-invert mx-auto max-w-none text-foreground/90 leading-relaxed font-sans">
        <div dangerouslySetInnerHTML={{ __html: post.content || "" }} />
      </div>

      {}
      <div className="mt-12 flex flex-col gap-6 border-t border-border pt-8 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2">
          {post.tags && post.tags.map((tag, i) => (
            <Link key={i} to={tag.link || "#"}>
              <span className="flex items-center gap-1 rounded-none bg-muted px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground transition-all hover:bg-foreground hover:text-background">
                <Tag className="h-3 w-3" />
                {tag.name}
              </span>
            </Link>
          ))}
        </div>
        
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Share Project:</span>
          <Button variant="outline" size="icon" className="rounded-none border-border hover:bg-foreground hover:text-background transition-all">
            <Share2 className="h-4 w-4" />
          </Button>
          {}
        </div>
      </div>

      {}
      {post.author && (
        <div className="mt-12 rounded-none bg-muted/50 p-8 border border-border">
          <div className="flex flex-col items-center gap-6 text-center md:flex-row md:text-left">
            <Avatar className="h-24 w-24 border border-foreground rounded-none shadow-none">
              <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} className="rounded-none" />
              <AvatarFallback className="rounded-none"><User className="h-8 w-8" /></AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-foreground uppercase tracking-tighter">{post.author.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {post.author.description || `Senior Project Lead at SolidTrust Builders. Specialist in North West London loft conversions, luxury home extensions, and high-end residential renovations.`}
              </p>
              {post.author.link && (
                <Link to={post.author.link} className="mt-4 inline-flex items-center text-xs font-bold uppercase tracking-widest text-foreground hover:opacity-70 transition-opacity">
                  View Portfolio <ChevronRight className="ml-1 h-3 w-3" />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {}
      <PostsProvider wp_query={relatedQuery}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </article>
  );
}



export default function SinglePostTemplateSection() {
  
  
  const currentWPQueryParams = (typeof window !== 'undefined' && (window as any).wvcClient) 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: 'post', p: 1 }; 

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background text-foreground w-full">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}